import { SetStateAction } from 'react';

export const firstPage = 1;

export type DataSourceSortDirection = 'asc' | 'desc';

export type ItemKeyCallback<TData> = (item: TData) => string | number;

export type ShouldRetryFunction<TError = unknown> = (
    failureCount: number,
    error: TError,
) => boolean;
export type RetryValue<TError> = boolean | number | ShouldRetryFunction<TError>;

/**
 * Sort info.
 */
export type DataSourceSort = Readonly<{
    /**
     * The field to sort.
     */
    field: string;

    /**
     * The direction of the sort.
     */
    dir: DataSourceSortDirection;
}>;

/**
 * Paging info.
 */
export type DataSourcePaging = Readonly<{
    /**
     * The current page.
     */
    page: number;

    /**
     * The page size.
     */
    pageLimit: number;

    /**
     * The total number of pages.
     */
    totalPages: number | undefined;

    /**
     * The total number of results.
     */
    totalResults: number | undefined;
}>;

/**
 * The result of a data source query.
 */
export type DataSourceQueryResult<TData> = Readonly<{
    /**
     * The current page.
     */
    currentPage: number;

    /**
     * Total page count.
     */
    totalPages: number;

    /**
     * The current page of results.
     */
    results: TData[];

    /**
     * Total result count.
     */
    totalResults: number;
}>;

/**
 * The data passed to a data source query function.
 */
export type DataSourceRequest<TFilter> = Readonly<{
    /**
     * The current filter.
     */
    filter: TFilter;

    /**
     * The current sort config.
     */
    sort: DataSourceSort[];

    /**
     * The current page.
     */
    page: number;

    /**
     * The page size.
     */
    pageLimit: number;
}>;

export type DataSourceQuery<TData, TFilter> = (
    info: DataSourceRequest<TFilter>,
) => Promise<DataSourceQueryResult<TData>>;

/**
 * Data source configuration options.
 */
export type DataSourceOptions<TData, TFilter, TError = unknown> = Readonly<{
    /**
     * The name of the data source.
     */
    name: string;

    /**
     * If disabled the data source will not execute the query (defaults to `true`).
     */
    enabled?: boolean;

    /**
     * If true, the data source will keep the previous data when fetching new data.
     */
    keepPreviousData?: boolean;

    /**
     * The starting filter.  Can be any type.
     */
    defaultFilter: TFilter;

    /**
     * The initial sort config.  Can be any type.
     */
    defaultSort?: DataSourceSort[];

    /**
     * The page size to use. Overrides the user preference page size from user context.
     */
    pageLimit?: number;

    /**
     * A callback used to retrieve a page of results.
     */
    query: DataSourceQuery<TData, TFilter>;

    /**
     * Whether to retry fetching the data on failure
     */
    retry?: RetryValue<TError>;

    /**
     * The interval in milliseconds to automatically refetch data
     */
    refetchInterval?: number | false;

    /**
     * The time in milliseconds that data remains fresh
     */
    staleTime?: number;

    /**
     * Whether to refetch data when the window regains focus
     */
    refetchOnWindowFocus?: boolean;
}>;

export type BaseDataSourceOptions<TData, TFilter> = Pick<
    DataSourceOptions<TData, TFilter>,
    | 'name'
    | 'defaultFilter'
    | 'defaultSort'
    | 'enabled'
    | 'pageLimit'
    | 'keepPreviousData'
    | 'refetchInterval'
    | 'staleTime'
    | 'refetchOnWindowFocus'
>;

export type DataSource<TData, TFilter> = Readonly<{
    /**
     * The name of the data source (used as the TanStack Query cache key prefix).
     */
    name: string;

    /**
     * The time in milliseconds that data remains fresh.
     */
    staleTime?: number;

    /**
     * The current filter value.
     */
    filter: TFilter;

    /**
     * The sorting config.
     */
    sort: DataSourceSort[];

    /**
     * The current paging info.
     */
    paging: DataSourcePaging;

    /**
     * Current page of data.
     */
    results: TData[] | undefined;

    /**
     * If the data source is currently loading data.
     */
    isLoading: boolean;

    /**
     * The current error.
     */
    error: unknown;

    /**
     * Used to change the current filter options.
     *
     * @param filterValue A filter object or state setter function.
     *
     * @example
     * data.setFilter({ name: 'foo' });
     * data.setFilter((filter) => ({ ...filter, name: 'foo' }));
     */
    setFilter: (filterValue: SetStateAction<TFilter>) => void;

    /**
     * Used to change the sort options.
     *
     * @param sortValue Sort config or state setter function.
     *
     * @example
     * data.setSort([{ field: 'Name', dir: 'asc' }]);
     * data.setSort((sort) => [...sort, { field: 'Id', dir: 'asc' }]);
     */
    setSort: (sortValue: SetStateAction<DataSourceSort[]>) => void;

    /**
     * Resets filter, sort, and paging to default values.
     */
    reset: () => void;

    /**
     * Used to replace stale items with updated items.
     *
     * @param items List of updated items.
     * @param itemKey Used to determine which items to replace.
     *
     * @example
     * itemDataSource.updateData(
     *   [{ Id: 1, Description: 'Item 1' }],
     *   (item) => item.Id
     * );
     */
    updateData: (items: TData[], itemKey: ItemKeyCallback<TData>) => void;
}>;

/**
 * Replaces items in `currentItems` array with items in `updatedItems` array.
 * Returns a new array.
 *
 * @param currentItems The current list of items.
 * @param updatedItems The updated items.
 * @param itemKey Used to generate a unique key for an item.
 * @returns A new item array with replaced items.
 */
export function replaceItems<TItem>(
    currentItems: TItem[],
    updatedItems: TItem[],
    itemKey: ItemKeyCallback<TItem>,
) {
    const existingItemsAndKeys = currentItems.map((item) => ({
        item,
        key: itemKey(item),
    }));
    const newItemsAndKeys = updatedItems.map((item) => ({ item, key: itemKey(item) }));

    return newItemsAndKeys
        .reduce(
            (results, newItem) =>
                results.map((item) => (item.key === newItem.key ? newItem : item)),
            existingItemsAndKeys,
        )
        .map(({ item }) => item);
}

type DataSourceSortBuilder = {
    thenBy: (field: string, dir: DataSourceSortDirection) => DataSourceSortBuilder;
    value: DataSourceSort[];
};

type Path<
    T,
    K extends Extract<keyof T, string>,
    TCtx extends string = '',
> = TCtx extends '' ? K : `${TCtx}.${K}`;

type NestedKeys<T, TCtx extends string = ''> =
    | TCtx
    | (T extends object
          ? {
                [K in Extract<keyof T, string>]:
                    | Path<T, K, TCtx>
                    | (T[K] extends object ? NestedKeys<T[K], Path<T, K, TCtx>> : never);
            }[Extract<keyof T, string>]
          : never);

export function dataSourceOrderBy<T>(
    field: NestedKeys<T>,
    dir: DataSourceSortDirection = 'asc',
): DataSourceSortBuilder {
    const sorts: DataSourceSort[] = [{ field, dir }];
    const builder: DataSourceSortBuilder = {
        thenBy: (field, dir) => {
            sorts.push({ field, dir });
            return builder;
        },
        get value() {
            return sorts;
        },
    };
    return builder;
}
